import { useEffect, useState, useRef, useCallback } from 'react'

const MFA_SETUP_TIMEOUT_MINUTES = 5

interface UseMfaAbortTimerParams {
  durationMinutes?: number
  onTimeout: () => void
}

export const useMfaAbortTimer = ({
  durationMinutes = MFA_SETUP_TIMEOUT_MINUTES,
  onTimeout,
}: UseMfaAbortTimerParams) => {
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const endTime = useRef(Date.now() + durationMinutes * 60 * 1000)
  const timeoutTriggered = useRef(false)

  const tick = useCallback(() => {
    const remaining = Math.max(
      0,
      Math.floor((endTime.current - Date.now()) / 1000),
    )
    setTimeLeft(remaining)

    if (remaining === 0 && !timeoutTriggered.current) {
      timeoutTriggered.current = true
      onTimeout()
    }
  }, [onTimeout])

  useEffect(() => {
    if (durationMinutes <= 0) return

    const timeout = setTimeout(() => {
      if (!timeoutTriggered.current) {
        timeoutTriggered.current = true
        onTimeout()
      }
    }, durationMinutes * 60 * 1000)

    const lastMinuteStartDelay = (durationMinutes * 60 - 60) * 1000
    const lastMinuteTimer = setTimeout(() => {
      tick()

      const interval = setInterval(tick, 1000)

      setTimeout(() => {
        clearInterval(interval)
      }, 60 * 1000)
    }, lastMinuteStartDelay)

    return () => {
      clearTimeout(timeout)
      clearTimeout(lastMinuteTimer)
    }
  }, [durationMinutes, onTimeout, tick])

  return {
    timeLeft,
    isLastMinute: timeLeft !== null && timeLeft <= 60,
  }
}
